import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, ChevronLeft, Edit2, X, Check, Zap, Clock, Loader2,
  Coins, TrendingUp, Activity, AlertCircle, Sprout, Wallet,
  ArrowLeftRight, CheckCircle2,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth.store';

const inputCls = 'w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/40 transition-colors placeholder-slate-700';

function StatusBadge({ status }: { status: string }) {
  const active = status === 'ACTIVE';
  const pending = status === 'PENDING';
  const cls = active
    ? 'badge-active'
    : pending
    ? 'bg-amber-500/15 border border-amber-500/25 text-amber-400'
    : 'badge-inactive';
  const dotCls = active ? 'bg-emerald-400 pulse-dot' : pending ? 'bg-amber-400' : 'bg-slate-500';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`} />
      {status}
    </span>
  );
}

/* ─── World Currency Conversion Section ─── */
function WorldCurrencyConvertSection({
  worldId,
  currencySymbol,
  accounts,
}: {
  worldId: string;
  currencySymbol: string;
  accounts: { accountType: string; balance: string }[];
}) {
  const qc = useQueryClient();

  const p2eBalance = parseFloat(accounts.find((a) => a.accountType === 'P2E_POOL')?.balance ?? '0');
  const revenueBalance = parseFloat(accounts.find((a) => a.accountType === 'OWNER_REVENUE')?.balance ?? '0');
  const cbAllocationBalance = parseFloat(accounts.find((a) => a.accountType === 'CB_ALLOCATION')?.balance ?? '0');

  const [direction, setDirection] = useState<'P2E_TO_UKC' | 'UKC_TO_P2E' | 'REVENUE_TO_UKC'>('P2E_TO_UKC');
  const [amount, setAmount] = useState('');
  const [success, setSuccess] = useState<{ sendAmount: string; receiveAmount: string; sendCurrency: string; receiveCurrency: string } | null>(null);

  const { data: rate } = useQuery<{ ukcPerUnit: number } | null>({
    queryKey: ['exchange-rate', worldId],
    queryFn: () => api.get(`/exchange/rates/${worldId}`).then((r) => r.data).catch(() => null),
  });

  const DIRECTIONS = [
    { value: 'P2E_TO_UKC', label: `P2E Pool → UKC`, available: p2eBalance, availCurrency: currencySymbol },
    { value: 'REVENUE_TO_UKC', label: `Revenue → UKC`, available: revenueBalance, availCurrency: currencySymbol },
    { value: 'UKC_TO_P2E', label: `UKC → P2E Pool`, available: cbAllocationBalance, availCurrency: 'UKC' },
  ] as const;

  const activeDir = DIRECTIONS.find((d) => d.value === direction)!;
  const spendCurrency = direction === 'UKC_TO_P2E' ? 'UKC' : currencySymbol;
  const receiveCurrency = direction === 'UKC_TO_P2E' ? currencySymbol : 'UKC';

  const previewReceive = (() => {
    if (!amount || !rate) return null;
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) return null;
    return direction === 'UKC_TO_P2E'
      ? (amt / rate.ukcPerUnit).toFixed(8)
      : (amt * rate.ukcPerUnit).toFixed(8);
  })();

  const mutation = useMutation({
    mutationFn: (data: { direction: string; amount: string }) =>
      api.post(`/central-bank/convert-world/${worldId}`, data).then((r) => r.data),
    onSuccess: (data) => {
      setSuccess(data);
      setAmount('');
      qc.invalidateQueries({ queryKey: ['world-accounts', worldId] });
      qc.invalidateQueries({ queryKey: ['world', worldId] });
    },
  });

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="glass-card overflow-hidden" style={{ borderColor: 'rgba(139,92,246,0.2)' }}>
      <div className="h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
      <div className="flex items-center gap-2 px-5 py-4">
        <ArrowLeftRight className="w-4 h-4 text-purple-400" />
        <h3 className="text-sm font-semibold text-white">World Currency Conversion</h3>
        <span className="text-[10px] bg-purple-500/10 border border-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full ml-auto">World Owner / CB Admin</span>
      </div>

      <div className="px-5 pb-5 space-y-5">
        {/* Account balances summary */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'P2E Pool', value: p2eBalance, currency: currencySymbol, color: 'text-emerald-400' },
            { label: 'Owner Revenue', value: revenueBalance, currency: currencySymbol, color: 'text-purple-400' },
            { label: 'CB Allocation', value: cbAllocationBalance, currency: 'UKC', color: 'text-cyan-400' },
          ].map(({ label, value, currency, color }) => (
            <div key={label} className="bg-white/[0.02] border border-white/[0.05] rounded-xl px-3 py-2.5">
              <p className="text-[9px] font-medium uppercase tracking-wider text-slate-600 mb-0.5">{label}</p>
              <p className={`text-sm font-bold font-mono ${color}`}>{value.toLocaleString('en-GB', { maximumFractionDigits: 4 })}</p>
              <p className="text-[9px] text-slate-700">{currency}</p>
            </div>
          ))}
        </div>

        {success ? (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4" /> Conversion successful
            </div>
            <div className="p-3 rounded-xl bg-emerald-500/[0.06] border border-emerald-500/15 text-xs text-slate-400 space-y-1">
              <div className="flex justify-between">
                <span>Sold</span>
                <span className="font-mono text-red-400">{parseFloat(success.sendAmount).toLocaleString(undefined, { maximumFractionDigits: 8 })} {success.sendCurrency}</span>
              </div>
              <div className="flex justify-between">
                <span>Received</span>
                <span className="font-mono text-emerald-400 font-semibold">{parseFloat(success.receiveAmount).toLocaleString(undefined, { maximumFractionDigits: 8 })} {success.receiveCurrency}</span>
              </div>
            </div>
            <button onClick={() => setSuccess(null)} className="w-full btn-primary py-2.5 rounded-xl text-sm font-semibold">New Conversion</button>
          </motion.div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); mutation.mutate({ direction, amount }); }} className="space-y-4">
            {/* Direction */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Direction</label>
              <div className="grid grid-cols-3 gap-2">
                {DIRECTIONS.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => { setDirection(d.value); setAmount(''); }}
                    className={`py-2 rounded-xl border text-[10px] font-semibold transition-all ${direction === d.value ? 'bg-purple-500/15 border-purple-500/35 text-purple-300' : 'border-white/8 text-slate-500 hover:border-white/15'}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Amount ({spendCurrency}){' '}
                <span className="text-slate-700 normal-case">— available: {activeDir.available.toLocaleString('en-GB', { maximumFractionDigits: 4 })} {activeDir.availCurrency}</span>
              </label>
              <input
                type="number"
                min="0.00000001"
                step="0.00000001"
                max={activeDir.available}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 1000.00"
                required
                className={inputCls}
              />
              {!rate && <p className="text-[10px] text-amber-600 mt-1">No exchange rate set for this world</p>}
            </div>

            {/* Preview */}
            {previewReceive && (
              <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="p-3.5 rounded-xl bg-purple-500/[0.05] border border-purple-500/20 space-y-2">
                <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider">Conversion Preview</p>
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <p className="font-mono text-sm font-bold text-white">{parseFloat(amount).toFixed(4)} {spendCurrency}</p>
                    <p className="text-[10px] text-slate-600">You spend</p>
                  </div>
                  <ArrowLeftRight className="w-4 h-4 text-purple-500/60 flex-shrink-0" />
                  <div className="text-center">
                    <p className="font-mono text-sm font-bold text-emerald-400">{parseFloat(previewReceive).toFixed(4)} {receiveCurrency}</p>
                    <p className="text-[10px] text-slate-600">You receive</p>
                  </div>
                </div>
                {rate && <p className="text-[10px] text-slate-600 text-center">Rate: 1 {currencySymbol} = {rate.ukcPerUnit} UKC</p>}
              </motion.div>
            )}

            {mutation.isError && (
              <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Conversion failed.'}
              </p>
            )}

            <button
              type="submit"
              disabled={mutation.isPending || !rate || !amount}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold bg-purple-500/15 border border-purple-500/25 text-purple-300 hover:bg-purple-500/25 transition-all disabled:opacity-40"
            >
              {mutation.isPending ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Converting…</>
              ) : (
                <><ArrowLeftRight className="w-4 h-4" /> Execute Conversion</>
              )}
            </button>
          </form>
        )}
      </div>
    </motion.div>
  );
}

/* ─── World Holder Exchange Section ─── */
function WorldHolderExchangeSection({ worldId, worldName, currencySymbol }: { worldId: string; worldName: string; currencySymbol: string }) {
  const qc = useQueryClient();
  const [holderId, setHolderId] = useState('');
  const [destWorldId, setDestWorldId] = useState('');
  const [amount, setAmount] = useState('');
  const [success, setSuccess] = useState<{ receiveAmount: string; destWorldId: string } | null>(null);

  const { data: allHolders = [] } = useQuery<{ id: string; name: string; worldIds: string[]; balances: { worldId: string; balance: string }[] }[]>({
    queryKey: ['holders'],
    queryFn: () => api.get('/holders').then((r) => r.data),
  });

  const { data: worlds = [] } = useQuery<{ id: string; name: string; currencySymbol: string; status: string }[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  const worldHolders = allHolders.filter((h) => h.worldIds.includes(worldId));
  const selectedHolder = worldHolders.find((h) => h.id === holderId);
  const sourceBalance = selectedHolder?.balances.find((b) => b.worldId === worldId);
  const available = parseFloat(sourceBalance?.balance ?? '0');

  const { data: sourceRate } = useQuery<{ ukcPerUnit: number } | null>({
    queryKey: ['exchange-rate', worldId],
    queryFn: () => api.get(`/exchange/rates/${worldId}`).then((r) => r.data).catch(() => null),
    enabled: !!worldId,
  });

  const { data: destRate } = useQuery<{ ukcPerUnit: number } | null>({
    queryKey: ['exchange-rate', destWorldId],
    queryFn: () => destWorldId ? api.get(`/exchange/rates/${destWorldId}`).then((r) => r.data).catch(() => null) : Promise.resolve(null),
    enabled: !!destWorldId,
  });

  const destWorld = worlds.find((w) => w.id === destWorldId);

  const previewAmount = (() => {
    if (!amount || !sourceRate || !destRate) return null;
    const send = parseFloat(amount);
    if (isNaN(send) || send <= 0) return null;
    return (send * (sourceRate.ukcPerUnit / destRate.ukcPerUnit)).toFixed(8);
  })();

  const mutation = useMutation({
    mutationFn: () => api.post(`/holders/${holderId}/exchange`, { sourceWorldId: worldId, destWorldId, amount }).then((r) => r.data),
    onSuccess: (data) => {
      setSuccess({ receiveAmount: data.receiveAmount, destWorldId: data.destWorldId });
      setAmount('');
      qc.invalidateQueries({ queryKey: ['holders'] });
    },
  });

  const availableDestWorlds = worlds.filter((w) => w.status === 'ACTIVE' && w.id !== worldId);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card overflow-hidden" style={{ borderColor: 'rgba(0,212,255,0.15)' }}>
      <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
      <div className="flex items-center gap-2 px-5 py-4">
        <ArrowLeftRight className="w-4 h-4 text-cyan-400" />
        <h3 className="text-sm font-semibold text-white">Holder Currency Exchange</h3>
        <span className="text-[10px] bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full ml-auto">
          Source: {currencySymbol}
        </span>
      </div>

      <div className="px-5 pb-5">
        {worldHolders.length === 0 ? (
          <p className="text-xs text-slate-600">No holders are associated with {worldName}.</p>
        ) : success ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4" />
              Exchange queued successfully
            </div>
            <p className="text-xs text-slate-500">
              Holder will receive{' '}
              <span className="text-emerald-400 font-mono">{parseFloat(success.receiveAmount).toFixed(8)} UKC</span>{' '}
              in <span className="text-cyan-400">{worlds.find((w) => w.id === success.destWorldId)?.name ?? success.destWorldId.slice(0, 8)}</span>.
            </p>
            <button onClick={() => setSuccess(null)} className="btn-primary text-xs px-5 py-2 rounded-xl">New Exchange</button>
          </motion.div>
        ) : (
          <form
            className="grid grid-cols-1 lg:grid-cols-4 gap-3 items-end"
            onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}
          >
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Holder</label>
              <select value={holderId} onChange={(e) => setHolderId(e.target.value)} required className={inputCls + ' appearance-none'}>
                <option value="">Select holder…</option>
                {worldHolders.map((h) => {
                  const bal = parseFloat(h.balances.find((b) => b.worldId === worldId)?.balance ?? '0');
                  return <option key={h.id} value={h.id}>{h.name} — {bal.toFixed(4)} UKC</option>;
                })}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Destination World</label>
              <select value={destWorldId} onChange={(e) => setDestWorldId(e.target.value)} required className={inputCls + ' appearance-none'}>
                <option value="">Select world…</option>
                {availableDestWorlds.map((w) => (
                  <option key={w.id} value={w.id}>{w.name} ({w.currencySymbol})</option>
                ))}
              </select>
              {destWorldId && !destRate && (
                <p className="text-[10px] text-amber-500 mt-1">No exchange rate set for this world</p>
              )}
            </div>

            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Amount (UKC){sourceBalance && <span className="text-slate-700 normal-case"> — max {available.toFixed(4)}</span>}
              </label>
              <input
                type="number"
                min="0.00000001"
                step="0.00000001"
                max={available || undefined}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                required
                disabled={!holderId}
                className={inputCls}
              />
            </div>

            <div className="space-y-2">
              {previewAmount && destWorld && (
                <p className="text-xs text-slate-400">
                  → <span className="text-emerald-400 font-mono font-semibold">{parseFloat(previewAmount).toFixed(4)}</span> UKC in {destWorld.name}
                </p>
              )}
              {!sourceRate && <p className="text-[10px] text-amber-500">No exchange rate set for {worldName}</p>}
              <button
                type="submit"
                disabled={mutation.isPending || !sourceRate || !destRate || !holderId}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-xs font-semibold rounded-xl bg-cyan-500/15 border border-cyan-500/25 text-cyan-300 hover:bg-cyan-500/25 disabled:opacity-40 transition-all"
              >
                {mutation.isPending
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing…</>
                  : <><ArrowLeftRight className="w-3.5 h-3.5" /> Execute Exchange</>}
              </button>
            </div>

            {mutation.isError && (
              <div className="col-span-full">
                <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                  {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Exchange failed.'}
                </p>
              </div>
            )}
          </form>
        )}
      </div>
    </motion.div>
  );
}

/* ─── Foreign Currency Balances Section ─── */
function ForeignBalancesSection({
  worldId,
  currencySymbol,
  foreignBalances,
  allWorlds,
  isReadOnly,
}: {
  worldId: string;
  currencySymbol: string;
  foreignBalances: { id: string; sourceWorldId: string; sourceWorldName: string; currency: string; balance: string; updatedAt: string }[];
  allWorlds: { id: string; name: string; currencySymbol: string }[];
  isReadOnly: boolean;
}) {
  const qc = useQueryClient();
  const [convertSourceWorldId, setConvertSourceWorldId] = useState('');
  const [convertAmt, setConvertAmt] = useState('');
  const [convertSuccess, setConvertSuccess] = useState<Record<string, string> | null>(null);

  const convertMutation = useMutation({
    mutationFn: (data: { direction: string; amount: string; sourceWorldId: string }) =>
      api.post(`/central-bank/convert-world/${worldId}`, data).then((r) => r.data),
    onSuccess: (data) => {
      setConvertSuccess(data);
      setConvertAmt('');
      qc.invalidateQueries({ queryKey: ['world-all-balances', worldId] });
      qc.invalidateQueries({ queryKey: ['world-accounts', worldId] });
    },
    onError: (e: unknown) => {
      alert((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Conversion failed');
    },
  });

  if (foreignBalances.length === 0) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="glass-card overflow-hidden" style={{ borderColor: 'rgba(245,158,11,0.2)' }}>
      <div className="h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
      <div className="flex items-center gap-2 px-5 py-4">
        <Coins className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-semibold text-white">Foreign Currency Holdings</h3>
        <span className="text-[10px] bg-amber-500/10 border border-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full ml-auto">Received from partner worlds</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              {['Source World', 'Currency', 'Balance', 'Last Updated', ...(isReadOnly ? [] : [''])].map((h, i) => (
                <th key={i} className="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-widest text-slate-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {foreignBalances.map((fb) => (
              <tr key={fb.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }} className="table-row-hover">
                <td className="px-5 py-3 text-slate-300">{fb.sourceWorldName}</td>
                <td className="px-5 py-3 font-mono text-amber-400 font-semibold">{fb.currency}</td>
                <td className="px-5 py-3 font-mono text-white">{Number(fb.balance).toLocaleString('en-GB', { maximumFractionDigits: 4 })}</td>
                <td className="px-5 py-3 text-slate-600">{new Date(fb.updatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                {!isReadOnly && (
                  <td className="px-5 py-3">
                    <button
                      onClick={() => { setConvertSourceWorldId(fb.sourceWorldId); setConvertSuccess(null); setConvertAmt(''); }}
                      className="text-[10px] px-2 py-1 rounded-lg border border-purple-500/20 text-purple-400 hover:bg-purple-500/10 transition-colors"
                    >
                      Convert → UKC
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Inline convert form for FOREIGN_TO_UKC */}
      {!isReadOnly && convertSourceWorldId && (() => {
        const activeFb = foreignBalances.find((fb) => fb.sourceWorldId === convertSourceWorldId);
        return (
          <div className="px-5 py-4 border-t border-white/5 bg-white/2">
            {convertSuccess ? (
              <div className="flex items-center gap-2 text-xs text-emerald-400">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Converted {convertSuccess['sendAmount']} {convertSuccess['sendCurrency']} → {convertSuccess['receiveAmount']} UKC
                <button onClick={() => { setConvertSuccess(null); setConvertSourceWorldId(''); }} className="ml-2 text-slate-600 hover:text-slate-400">✕</button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                  Amount ({activeFb?.currency ?? '?'}){' '}
                  {activeFb && (
                    <span className="text-slate-700 normal-case">
                      — available: {Number(activeFb.balance).toLocaleString('en-GB', { maximumFractionDigits: 4 })} {activeFb.currency}{' '}
                      from <span className="text-amber-400">{allWorlds.find((w) => w.id === convertSourceWorldId)?.name}</span>
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min="0" step="any"
                    placeholder={`e.g. 10 ${activeFb?.currency ?? ''}`}
                    max={activeFb ? Number(activeFb.balance) : undefined}
                    value={convertAmt} onChange={(e) => setConvertAmt(e.target.value)}
                    className="w-36 text-xs bg-[#0b1120] border border-white/10 rounded-lg px-2 py-1.5 text-slate-300 focus:outline-none focus:border-purple-500/40"
                  />
                  <button
                    disabled={convertMutation.isPending || !convertAmt}
                    onClick={() => convertMutation.mutate({ direction: 'FOREIGN_TO_UKC', amount: convertAmt, sourceWorldId: convertSourceWorldId })}
                    className="text-xs px-3 py-1.5 rounded-lg bg-purple-500/15 border border-purple-500/25 text-purple-300 hover:bg-purple-500/25 transition-colors disabled:opacity-50"
                  >
                    {convertMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : `Convert ${activeFb?.currency ?? ''} → UKC`}
                  </button>
                  <button onClick={() => setConvertSourceWorldId('')} className="text-xs text-slate-600 hover:text-slate-400">Cancel</button>
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </motion.div>
  );
}

/* ─── Partner Ops: Fund Holder + Send Gift ─── */
function WorldPartnerOpsSection({
  worldId,
  currencySymbol,
  allWorlds,
}: {
  worldId: string;
  currencySymbol: string;
  allWorlds: { id: string; name: string; currencySymbol: string; status: string }[];
}) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'fund' | 'gift'>('fund');

  // Fund holder state
  const [fundHolderId, setFundHolderId] = useState('');
  const [fundAmount, setFundAmount] = useState('');
  const [fundNote, setFundNote] = useState('');
  const [fundSuccess, setFundSuccess] = useState<Record<string, string> | null>(null);

  // Send gift state
  const [giftToType, setGiftToType] = useState<'WORLD' | 'CB'>('CB');
  const [giftToWorldId, setGiftToWorldId] = useState('');
  const [giftAmount, setGiftAmount] = useState('');
  const [giftReason, setGiftReason] = useState('');
  const [giftSuccess, setGiftSuccess] = useState<Record<string, string> | null>(null);

  const { data: allHolders = [] } = useQuery<{ id: string; name: string; email: string }[]>({
    queryKey: ['holders'],
    queryFn: () => api.get('/holders').then((r) => r.data),
  });

  const fundMutation = useMutation({
    mutationFn: (data: { holderId: string; amount: string; note?: string }) =>
      api.post(`/central-bank/worlds/${worldId}/fund-holder`, data).then((r) => r.data),
    onSuccess: (data) => {
      setFundSuccess(data);
      setFundHolderId(''); setFundAmount(''); setFundNote('');
      qc.invalidateQueries({ queryKey: ['world-accounts', worldId] });
      qc.invalidateQueries({ queryKey: ['world-all-balances', worldId] });
    },
    onError: (e: unknown) => {
      alert((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Fund holder failed');
    },
  });

  const giftMutation = useMutation({
    mutationFn: (data: { toType: string; toId?: string; amount: string; reason?: string }) =>
      api.post(`/central-bank/worlds/${worldId}/send-gift`, data).then((r) => r.data),
    onSuccess: (data) => {
      setGiftSuccess(data);
      setGiftAmount(''); setGiftReason(''); setGiftToWorldId('');
      qc.invalidateQueries({ queryKey: ['world-accounts', worldId] });
      qc.invalidateQueries({ queryKey: ['world-all-balances', worldId] });
    },
    onError: (e: unknown) => {
      alert((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Send gift failed');
    },
  });

  const otherActiveWorlds = allWorlds.filter((w) => w.id !== worldId && w.status === 'ACTIVE');

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 }} className="glass-card overflow-hidden" style={{ borderColor: 'rgba(99,102,241,0.2)' }}>
      <div className="h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent" />

      {/* Tab selector */}
      <div className="flex border-b border-white/5">
        {([
          { key: 'fund', label: 'Fund Holder from P2E Pool', icon: Wallet },
          { key: 'gift', label: 'Send Gift from Revenue', icon: TrendingUp },
        ] as const).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-5 py-3.5 text-xs font-semibold transition-colors ${tab === key ? 'text-indigo-300 border-b-2 border-indigo-500/50' : 'text-slate-600 hover:text-slate-400'}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="px-5 py-5">
        {tab === 'fund' ? (
          fundSuccess ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle2 className="w-4 h-4" /> Holder funded</div>
              <p className="text-xs text-slate-500">Transferred <span className="text-white font-mono">{fundSuccess['amount']} {fundSuccess['currency']}</span> to <span className="text-white">{fundSuccess['holderName']}</span></p>
              <button onClick={() => setFundSuccess(null)} className="text-xs btn-primary px-3 py-1.5 rounded-xl">Fund Another</button>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); fundMutation.mutate({ holderId: fundHolderId, amount: fundAmount, note: fundNote || undefined }); }} className="space-y-4">
              <p className="text-xs text-slate-500">Transfer world currency ({currencySymbol}) from this world's P2E Pool directly to a holder's balance.</p>
              <div className="space-y-1">
                <label className="text-[10px] font-medium uppercase tracking-wider text-slate-600">Holder</label>
                <select value={fundHolderId} onChange={(e) => setFundHolderId(e.target.value)} required className={inputCls + ' text-sm'}>
                  <option value="">Select holder...</option>
                  {(allHolders as { id: string; name: string; email: string }[]).map((h) => (
                    <option key={h.id} value={h.id}>{h.name} ({h.email})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-medium uppercase tracking-wider text-slate-600">Amount ({currencySymbol})</label>
                  <input type="number" min="0.00000001" step="any" required value={fundAmount} onChange={(e) => setFundAmount(e.target.value)} placeholder="e.g. 100.00" className={inputCls + ' text-sm'} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-medium uppercase tracking-wider text-slate-600">Note (optional)</label>
                  <input type="text" value={fundNote} onChange={(e) => setFundNote(e.target.value)} placeholder="e.g. Quest bonus" className={inputCls + ' text-sm'} />
                </div>
              </div>
              <button type="submit" disabled={fundMutation.isPending || !fundHolderId || !fundAmount} className="btn-primary px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center gap-2">
                {fundMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />} Fund Holder
              </button>
            </form>
          )
        ) : (
          giftSuccess ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle2 className="w-4 h-4" /> Gift sent</div>
              <p className="text-xs text-slate-500">Sent <span className="text-white font-mono">{giftSuccess['amount']} {giftSuccess['currency']}</span> to <span className="text-white">{giftSuccess['toType'] === 'CB' ? 'Central Bank' : allWorlds.find((w) => w.id === giftSuccess['toId'])?.name ?? giftSuccess['toId']}</span></p>
              <button onClick={() => setGiftSuccess(null)} className="text-xs btn-primary px-3 py-1.5 rounded-xl">Send Another</button>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); giftMutation.mutate({ toType: giftToType, toId: giftToType === 'WORLD' ? giftToWorldId : undefined, amount: giftAmount, reason: giftReason || undefined }); }} className="space-y-4">
              <p className="text-xs text-slate-500">Send {currencySymbol} from this world's Owner Revenue to another world or the Central Bank.</p>
              <div className="space-y-1">
                <label className="text-[10px] font-medium uppercase tracking-wider text-slate-600">Recipient</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['CB', 'WORLD'] as const).map((t) => (
                    <button key={t} type="button" onClick={() => setGiftToType(t)}
                      className={`py-2 rounded-xl border text-xs font-semibold transition-all ${giftToType === t ? 'bg-indigo-500/15 border-indigo-500/35 text-indigo-300' : 'border-white/8 text-slate-500 hover:border-white/15'}`}>
                      {t === 'CB' ? 'Central Bank' : 'Another World'}
                    </button>
                  ))}
                </div>
              </div>
              {giftToType === 'WORLD' && (
                <div className="space-y-1">
                  <label className="text-[10px] font-medium uppercase tracking-wider text-slate-600">Target World</label>
                  <select value={giftToWorldId} onChange={(e) => setGiftToWorldId(e.target.value)} required className={inputCls + ' text-sm'}>
                    <option value="">Select world...</option>
                    {otherActiveWorlds.map((w) => <option key={w.id} value={w.id}>{w.name} ({w.currencySymbol})</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-medium uppercase tracking-wider text-slate-600">Amount ({currencySymbol})</label>
                  <input type="number" min="0.00000001" step="any" required value={giftAmount} onChange={(e) => setGiftAmount(e.target.value)} placeholder="e.g. 500.00" className={inputCls + ' text-sm'} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-medium uppercase tracking-wider text-slate-600">Reason (optional)</label>
                  <input type="text" value={giftReason} onChange={(e) => setGiftReason(e.target.value)} placeholder="e.g. Partnership bonus" className={inputCls + ' text-sm'} />
                </div>
              </div>
              <button type="submit" disabled={giftMutation.isPending || !giftAmount || (giftToType === 'WORLD' && !giftToWorldId)} className="bg-indigo-500/15 border border-indigo-500/25 text-indigo-300 hover:bg-indigo-500/25 transition-colors px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center gap-2">
                {giftMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />} Send Gift
              </button>
            </form>
          )
        )}
      </div>
    </motion.div>
  );
}

export function WorldDetailPage() {
  const { worldId } = useParams<{ worldId: string }>();
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.role);

  const [showEdit, setShowEdit] = useState(false);
  const [showSeed, setShowSeed] = useState(false);
  const [editName, setEditName] = useState('');
  const [editCurrencyName, setEditCurrencyName] = useState('');
  const [editRevenueShare, setEditRevenueShare] = useState('');
  const [editTaxShare, setEditTaxShare] = useState('');
  const [editTaxOwnerId, setEditTaxOwnerId] = useState('');
  const [seedAmount, setSeedAmount] = useState('');
  const [seedNote, setSeedNote] = useState('');

  const { data: world, isLoading } = useQuery({
    queryKey: ['world', worldId],
    queryFn: () => api.get(`/worlds/${worldId}`).then((r) => r.data),
  });

  const { data: metrics } = useQuery({
    queryKey: ['world-metrics', worldId],
    queryFn: () => api.get(`/analytics/worlds/${worldId}/metrics`).then((r) => r.data).catch(() => null),
    enabled: !!worldId,
  });

  const { data: wsfHistory = [] } = useQuery({
    queryKey: ['wsf-history', worldId],
    queryFn: () => api.get(`/analytics/worlds/${worldId}/wsf-history`).then((r) => r.data).catch(() => []),
    enabled: !!worldId,
  });

  const { data: accounts = [] } = useQuery<{ accountType: string; balance: string }[]>({
    queryKey: ['world-accounts', worldId],
    queryFn: () => api.get(`/worlds/${worldId}/accounts`).then((r) => r.data).catch(() => []),
    enabled: !!worldId,
    refetchInterval: 30_000,
  });

  // All balances: native accounts + foreign currency holdings received from other worlds
  const { data: allBalances } = useQuery<{
    worldId: string; worldName: string; currencySymbol: string; currencyName: string;
    nativeAccounts: { accountType: string; balance: string; currency: string }[];
    foreignBalances: { id: string; sourceWorldId: string; sourceWorldName: string; currency: string; balance: string; updatedAt: string }[];
  }>({
    queryKey: ['world-all-balances', worldId],
    queryFn: () => api.get(`/central-bank/worlds/${worldId}/all-balances`).then((r) => r.data).catch(() => null),
    enabled: !!worldId,
    refetchInterval: 60_000,
  });

  const { data: allWorlds = [] } = useQuery<{ id: string; name: string; currencySymbol: string; status: string }[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  // Only initialise form fields when the edit panel *opens* (showEdit false→true).
  // Keeping `world` out of the trigger prevents a background React Query refetch
  // from silently overwriting whatever the user has already typed.
  const prevShowEdit = useRef(false);
  useEffect(() => {
    const opening = showEdit && !prevShowEdit.current;
    prevShowEdit.current = showEdit;
    if (opening && world) {
      setEditName(world.name);
      setEditCurrencyName(world.currencyName);
      setEditRevenueShare(world.revenueShare != null ? String(world.revenueShare) : '');
      setEditTaxShare(world.taxShare != null ? String(world.taxShare) : '');
      setEditTaxOwnerId(world.taxOwnerId ?? '');
    }
  }, [showEdit, world]);

  const editMutation = useMutation({
    mutationFn: (data: { name?: string; currencyName?: string; revenueShare?: number; taxShare?: number; taxOwnerId?: string }) =>
      api.patch(`/worlds/${worldId}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['world', worldId] });
      qc.invalidateQueries({ queryKey: ['worlds'] });
      // Also bust the fee-config cache used by the HoldersPage tip dialog
      qc.invalidateQueries({ queryKey: ['world-fee-config', worldId] });
      setShowEdit(false);
    },
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      api.patch(`/worlds/${worldId}/status`, { status }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['world', worldId] }),
  });

  const seedMutation = useMutation({
    mutationFn: (data: { worldId: string; amount: string; note?: string }) =>
      api.post('/central-bank/seed-world', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['world', worldId] });
      setShowSeed(false);
      setSeedAmount('');
      setSeedNote('');
    },
  });

  if (isLoading) {
    return (
      <div className="p-8 flex items-center gap-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading world...
      </div>
    );
  }
  if (!world) {
    return <div className="p-8 text-red-400">World not found</div>;
  }

  const canActivate = world.status === 'PENDING' || world.status === 'SUSPENDED';
  const canSuspend = world.status === 'ACTIVE';

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/worlds" className="text-slate-600 hover:text-slate-300 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/15 to-purple-500/15 border border-cyan-500/20 flex items-center justify-center">
              <Globe className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{world.name}</h2>
              <p className="text-xs text-slate-600 font-mono">{world.currencyName} ({world.currencySymbol})</p>
            </div>
          </div>
          <StatusBadge status={world.status} />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {role === 'CENTRAL_BANK_ADMIN' && (
            <button
              onClick={() => { setShowSeed(!showSeed); setShowEdit(false); }}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-xl border border-emerald-500/25 text-emerald-400 hover:bg-emerald-500/10 transition-all"
            >
              <Sprout className="w-3.5 h-3.5" /> Seed P2E Pool
            </button>
          )}
          <button
            onClick={() => { setShowEdit(!showEdit); setShowSeed(false); }}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-xl border border-white/10 text-slate-400 hover:text-white hover:border-white/20 transition-all"
          >
            <Edit2 className="w-3.5 h-3.5" /> Edit
          </button>
          {canActivate && (
            <button
              onClick={() => statusMutation.mutate('ACTIVE')}
              disabled={statusMutation.isPending}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl btn-primary"
            >
              {statusMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Activate
            </button>
          )}
          {canSuspend && (
            <button
              onClick={() => statusMutation.mutate('SUSPENDED')}
              disabled={statusMutation.isPending}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl bg-amber-500/15 border border-amber-500/25 text-amber-400 hover:bg-amber-500/25 transition-all"
            >
              {statusMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
              Suspend
            </button>
          )}
        </div>
      </div>

      {/* World status info banner */}
      {world.status === 'PENDING' && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 glass-card p-4"
          style={{ borderColor: 'rgba(245,158,11,0.25)' }}
        >
          <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-300">World is Pending</p>
            <p className="text-xs text-slate-500 mt-0.5">
              This world must be activated before it can process transactions or rewards. Click "Activate" to set it live.
            </p>
          </div>
        </motion.div>
      )}

      {/* Edit form */}
      <AnimatePresence>
        {showEdit && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="glass-card p-6 space-y-4 overflow-hidden"
            style={{ borderColor: 'rgba(0,212,255,0.2)' }}
            onSubmit={(e) => {
              e.preventDefault();
              const patch: { name?: string; currencyName?: string; revenueShare?: number; taxShare?: number; taxOwnerId?: string } = {};
              if (editName !== world.name) patch.name = editName;
              if (editCurrencyName !== world.currencyName) patch.currencyName = editCurrencyName;
              if (editRevenueShare !== '') patch.revenueShare = Number(editRevenueShare);
              if (editTaxShare !== '') patch.taxShare = Number(editTaxShare);
              patch.taxOwnerId = editTaxOwnerId; // always send; empty string clears the tax owner
              editMutation.mutate(patch);
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Edit World</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">World Name</label>
                <input value={editName} onChange={(e) => setEditName(e.target.value)} required minLength={3} maxLength={64} className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Currency Name</label>
                <input value={editCurrencyName} onChange={(e) => setEditCurrencyName(e.target.value)} required minLength={2} maxLength={50} className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Revenue Share % <span className="normal-case text-slate-600">(CB cut, 0–100)</span></label>
                <input
                  type="number" min="0" max="100" step="0.01"
                  value={editRevenueShare}
                  onChange={(e) => setEditRevenueShare(e.target.value)}
                  placeholder="e.g. 5.0"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Tax Share % <span className="normal-case text-slate-600">(0–100)</span></label>
                <input
                  type="number" min="0" max="100" step="0.01"
                  value={editTaxShare}
                  onChange={(e) => setEditTaxShare(e.target.value)}
                  placeholder="e.g. 2.5"
                  className={inputCls}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Tax Owner Holder ID <span className="normal-case text-slate-600">(leave blank to clear)</span></label>
                <input
                  value={editTaxOwnerId}
                  onChange={(e) => setEditTaxOwnerId(e.target.value)}
                  placeholder="Holder UUID — must be a member of this world"
                  className={inputCls}
                />
              </div>
            </div>
            <p className="text-[10px] text-slate-700">Currency symbol cannot be changed after creation.</p>
            {editMutation.isError && (
              <p className="text-red-400 text-xs">{(editMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Update failed'}</p>
            )}
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowEdit(false)} className="text-xs text-slate-400 px-4 py-2 rounded-xl border border-white/8 hover:border-white/15 transition-all">Cancel</button>
              <button type="submit" disabled={editMutation.isPending} className="btn-primary text-xs font-semibold px-5 py-2 rounded-xl flex items-center gap-2">
                {editMutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</> : <><Check className="w-3.5 h-3.5" /> Save Changes</>}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Seed P2E Pool form */}
      <AnimatePresence>
        {showSeed && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="glass-card p-6 space-y-4 overflow-hidden"
            style={{ borderColor: 'rgba(16,185,129,0.2)' }}
            onSubmit={(e) => {
              e.preventDefault();
              seedMutation.mutate({ worldId: worldId!, amount: seedAmount, note: seedNote || undefined });
            }}
          >
            <div className="flex items-center gap-2">
              <Sprout className="w-4 h-4 text-emerald-400" />
              <p className="text-sm font-semibold text-white">Seed P2E Pool</p>
            </div>
            <p className="text-xs text-slate-500">Transfer UKC from the Central Bank reserve into this world's Play-to-Earn pool.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Amount (UKC)</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={seedAmount}
                  onChange={(e) => setSeedAmount(e.target.value)}
                  placeholder="e.g. 500000"
                  required
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Note (optional)</label>
                <input value={seedNote} onChange={(e) => setSeedNote(e.target.value)} placeholder="e.g. Initial launch seeding" className={inputCls} />
              </div>
            </div>
            {seedMutation.isError && (
              <p className="text-red-400 text-xs">{(seedMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Seed failed'}</p>
            )}
            {seedMutation.isSuccess && (
              <p className="text-emerald-400 text-xs flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Seeded successfully</p>
            )}
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowSeed(false)} className="text-xs text-slate-400 px-4 py-2 rounded-xl border border-white/8 hover:border-white/15 transition-all">Cancel</button>
              <button type="submit" disabled={seedMutation.isPending} className="text-xs font-semibold px-5 py-2 rounded-xl flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/25 transition-all">
                {seedMutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Seeding...</> : <><Sprout className="w-3.5 h-3.5" /> Confirm Seed</>}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Daily Active Users', value: metrics?.dailyActiveUsers ?? '--', icon: Activity, color: 'text-cyan-400' },
          { label: 'Revenue per User', value: metrics?.revenuePerActiveUser ?? '--', icon: Coins, color: 'text-purple-400' },
          { label: '7d Retention', value: metrics?.retentionRate7d ? `${Number(metrics.retentionRate7d).toFixed(1)}%` : '--', icon: TrendingUp, color: 'text-emerald-400' },
          { label: 'Current WSF', value: world.scalingConfig?.currentWsf ?? '--', icon: Zap, color: 'text-amber-400', sub: 'Reward multiplier (0.2×–2.0×). Auto-recalculates every 15 min from P2E pool health and revenue velocity.' },
        ].map(({ label, value, icon: Icon, color, sub }) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Icon className={`w-3.5 h-3.5 ${color}`} />
              <p className="text-[10px] font-medium uppercase tracking-widest text-slate-600">{label}</p>
            </div>
            <p className={`text-xl font-bold ${color}`}>{value}</p>
            {sub && <p className="text-[10px] text-slate-600 mt-1.5 leading-relaxed">{sub}</p>}
          </motion.div>
        ))}
      </div>

      {/* World currency balances */}
      {accounts.length > 0 && (() => {
        const ACCOUNT_META: Record<string, { label: string; color: string; desc: string; currency: string }> = {
          P2E_POOL:       { label: 'P2E Pool',       color: 'text-emerald-400', desc: 'Play-to-earn reward reserve', currency: world.currencySymbol },
          OWNER_REVENUE:  { label: 'Owner Revenue',  color: 'text-purple-400',  desc: 'World owner earnings',        currency: world.currencySymbol },
          CB_ALLOCATION:  { label: 'CB Allocation',  color: 'text-cyan-400',    desc: 'Seeded by central bank',      currency: 'UKC' },
          FUTURE_RESERVE: { label: 'Future Reserve', color: 'text-amber-400',   desc: 'Long-term reserve',           currency: world.currencySymbol },
        };
        return (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="glass-card overflow-hidden">
            <div className="h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
            <div className="flex items-center gap-2 px-5 py-4">
              <Wallet className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-semibold text-white">World Balances</h3>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[10px] text-slate-600">{world.currencySymbol} · UKC</span>
              </div>
            </div>


            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.03]">
              {accounts.map((acc) => {
                const meta = ACCOUNT_META[acc.accountType] ?? { label: acc.accountType, color: 'text-slate-400', desc: '', currency: world.currencySymbol };
                const bal = parseFloat(acc.balance ?? '0');
                return (
                  <div key={acc.accountType} className="bg-[#0b1120]/60 px-5 py-4">
                    <p className="text-[10px] font-medium uppercase tracking-widest text-slate-600 mb-0.5">{meta.label}</p>
                    <p className={`text-xl font-bold font-mono ${meta.color}`}>{bal.toLocaleString('en-GB', { maximumFractionDigits: 2 })}</p>
                    <p className={`text-[10px] font-semibold mt-0.5 ${meta.currency === 'UKC' ? 'text-cyan-600' : 'text-slate-500'}`}>{meta.currency}</p>
                    <p className="text-[9px] text-slate-700 mt-0.5">{meta.desc}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        );
      })()}

      {/* Holder Currency Exchange */}
      {role !== 'ADMIN_READONLY' && worldId && accounts.length > 0 && (
        <WorldCurrencyConvertSection worldId={worldId} currencySymbol={world.currencySymbol} accounts={accounts} />
      )}

      {/* Holder Currency Exchange */}
      {role !== 'ADMIN_READONLY' && worldId && (
        <WorldHolderExchangeSection worldId={worldId} worldName={world.name} currencySymbol={world.currencySymbol} />
      )}

      {/* Foreign Currency Holdings */}
      {allBalances && allBalances.foreignBalances && (
        <ForeignBalancesSection
          worldId={worldId!}
          currencySymbol={world.currencySymbol}
          foreignBalances={allBalances.foreignBalances}
          allWorlds={allWorlds}
          isReadOnly={role === 'ADMIN_READONLY'}
        />
      )}

      {/* Fund Holder + Send Gift */}
      {role !== 'ADMIN_READONLY' && worldId && (
        <WorldPartnerOpsSection worldId={worldId} currencySymbol={world.currencySymbol} allWorlds={allWorlds} />
      )}
      <div className="glass-card p-5">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-4">World Details</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          {[
            { label: 'ID', value: world.id, mono: true },
            { label: 'Created', value: new Date(world.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) },
            { label: 'Owner ID', value: world.ownerId?.slice(0, 12) + '...', mono: true },
            { label: 'Revenue Share', value: `${world.revenueShare ?? 0}%`, mono: false },
            { label: 'Tax Share', value: `${world.taxShare ?? 0}%`, mono: false },
            { label: 'Tax Owner', value: world.taxOwnerId ? world.taxOwnerId.slice(0, 12) + '...' : '—', mono: !!world.taxOwnerId },
          ].map(({ label, value, mono }) => (
            <div key={label}>
              <p className="text-[10px] text-slate-600 mb-0.5">{label}</p>
              <p className={`text-slate-300 ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* WSF History */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="glass-card overflow-hidden"
      >
        <div className="h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-white">WSF History</h3>
          </div>
          <Link
            to={`/worlds/${worldId}/rules`}
            className="text-xs text-cyan-500 hover:text-cyan-300 transition-colors flex items-center gap-1"
          >
            Manage Rules →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                {['Scaling Factor', 'Revenue Velocity', 'P2E Health', 'Calculated At'].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-widest text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {wsfHistory.slice(0, 20).map((h: Record<string, string>, i: number) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }} className="table-row-hover">
                  <td className="px-5 py-3 text-amber-300 font-mono font-semibold">{parseFloat(h['wsfValue']).toFixed(4)}</td>
                  <td className="px-5 py-3 text-slate-300 font-mono">{parseFloat(h['revenueVelocityScore']).toFixed(4)}</td>
                  <td className="px-5 py-3 text-slate-300 font-mono">{parseFloat(h['p2eHealthScore']).toFixed(4)}</td>
                  <td className="px-5 py-3 text-slate-600">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      {new Date(h['calculatedAt']).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </td>
                </tr>
              ))}
              {wsfHistory.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-slate-600 text-xs">
                    No WSF history yet — activate the world to start accruing data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}